<template>
<div>
  <nav-bar></nav-bar>
  <div class="container">
    <div v-if="order">
      <h1 class="title has-text-centered">Order {{order.order_id}} <span v-if="order.quote_number">(#{{order.quote_number}})</span></h1>
      <section class="columns is-centered" v-if="order.status && orderId !== 'review'">
        <div class="column is-narrow message is-danger has-text-danger">
          This order is on hold. Reason: {{order.status}}
        </div>
      </section>
      <section class="order-header" v-if="orderHasBeenFetched">
            <details-header :order="order"></details-header>
      </section>
      <section>
        <div>
          <details-cell
            v-for="(detail, idx) in details"
            :key="idx"
            :number="idx+1"
            :columns="makeColumns(detail, category)"
          >
          </details-cell>
        </div>
          <div class="has-text-right has-margin-top-25 has-margin-bottom-25">
            Total Qty. {{totalQty}}
          </div>

      </section>
      <section v-if="additionalItems">
        <div class="ed-ai-header has-text-centered">
          <h1 class="subtitle">Additional Items</h1>
        </div>
        <article class="ed-ai-box box is-radiusless">
          <table class="table is-fullwidth is-striped">
            <tbody>
              <tr v-for="(item, idx) in additionalItems" :key="idx">
                <td>{{item.Description}}</td>
                <td>{{item.Qty}}</td>
              </tr>
            </tbody>
          </table>
        </article>
      </section>
      <section v-if="specialInstructions">
        <article class="ed-si-box box is-radiusless">
          Special instructions: {{specialInstructions}}
        </article>
      </section>
      <section v-if="relatedFiles">
        <article class="ed-si-box box is-radiusless">
          <h1 class="is-size-4">Documentation that might be helpful</h1>
          <p>
            <ul>
            <li class="file-item" v-for="file in relatedFiles" :key="file.id">
              <fa-icon icon="paperclip"/> {{file.name}} <a :href="`https://api.elitedecorinc.com/api/v1/files/${file.id}`" target="_blank">View/Download</a>
            </li>
          </ul>
          </p>
        </article>
      </section>
      <section v-if="orderId === 'review'" class="section">
      <p class="has-text-centered ">
        <form class="has-text-centered" id="submitItems" @submit.prevent="submit">
          <div class="buttons is-center">
            <a class="button" @click="$router.push({ path: 'items' });">Edit Order</a>
            <button type="submit" form="submitItems" class="button is-primary">submit</button>
          </div>
        </form>
      </p>
    </section>
    </div>
    <div v-else>
      <p class="has-text-centered">
        This page either does not exist or you do not have the necessary credentials to view.
        Please  <router-link :to="{ name: 'login', query: { redirect: $route.path }}">log in</router-link> to try again.
      </p>
    </div>
  </div>
  <ed-footer></ed-footer>
</div>
</template>

<script>
import DetailsHeader from '@/components/DetailsHeader.vue';
import DetailsCell from '@/components/DetailsCell.vue';
import Footer from '@/components/Footer.vue';
import { orderDetailsByCustomerAndOrder, submitOrder } from '@/utils/api';

export default {
  name: 'Order',
  components: {
    'details-header': DetailsHeader,
    'details-cell': DetailsCell,
    'ed-footer': Footer,
  },
  props: {
    customerId: String,
    orderId: String,
  },
  async created() {
    await this.loadData();
  },
  watch: {
    async $route() {
      await this.loadData();
    },
  },
  data() {
    return {
      orderHasBeenFetched: false,
      order: null,
      details: null,
      category: null,
      specialInstructions: null,
      additionalItems: null,
      relatedFiles: null,
      rep: {},
      orderDate: '',
      columns: null,
    };
  },
  computed: {
    totalQty() {
      return this.details.reduce((accum, curr) => accum + curr.Qty, 0);
    },
  },
  methods: {
    async loadData() {
      // if this is a review, just load review
      let data;
      if (this.orderId === 'review') {
        const category = JSON.parse(window.localStorage.getItem('ed-cart-category'));
        const items = JSON.parse(window.localStorage.getItem('ed-cart-items'));
        const order = {
          category,
          order_id: 'Review',
          po_sm: JSON.parse(window.localStorage.getItem('ed-cart-po')),
          quoted_amount: JSON.parse(window.localStorage.getItem('ed-cart-quote-amount')),
          quote_number: JSON.parse(window.localStorage.getItem('ed-cart-quote-number')),
        };
        console.log('items')
        console.log(items)
        const details = items.map(item => ({
          Qty: item.qty,
          Width: `${item.size.width} ${item.size.width_fraction || ''}`,
          Height: `${item.size.height} ${item.size.height_fraction || ''}`,
          IBOB: item.size.ibob,
          Room: item.room,
          Style: item.fabric.style,
          Color: item.fabric.color,
          'Fabric Position': item.fabric.roll,
          Chain: `${item.chain.color}-${(item.chain.material || 'P').charAt(0)}`,
          'Chain Length': item.chain.length,
          'Chain Control': item.chain.direction,
          Valance: item.valance.type,
          'Valance Color': item.valance.color,
        }));
        data = {
          category,
          details,
          ...order,
          order_date: Date.now(),
        };
      } else {
        ({ data } = await orderDetailsByCustomerAndOrder(this.customerId, this.orderId));
      }
      this.order = data;
      this.orderHasBeenFetched = true;
      this.category = data.category;
      this.details = data.details;
      this.specialInstructions = data.special_instructions;
      this.additionalItems = data.additional_items;
      this.relatedFiles = data.files;
      this.orderDate = data.order_date;
      this.rep = { name: data.rep_name, email: data.rep_email };
    },
    removeEmptyFields(cols) {
      const newCols = cols.map(col => (Object.entries(col)
        .filter(([, val]) => val !== '' && val !== undefined)
        .reduce((o, [key, val]) => ({ ...o, [key]: val }), {})
      )).filter(o => Object.keys(o).length > 0);
      return newCols;
    },
    getChainControl(field) {
      if (['R', 'right'].includes(field)) return 'Right';
      if (['L', 'left'].includes(field)) return 'Left';
      return '';
    },
    getChainType(field) {
      if (field) {
        const arr = field.split('-');
        if (arr.length === 2) {
          return arr[1] === 'M' ? 'Metal' : 'Plastic';
        }
      }
      return '';
    },
    getChainColor(field) {
      if (field) {
        const arr = field.split('-');
        if (arr.length >= 1) {
          return arr[0].replace(/^\w/, c => c.toUpperCase());
        }
      }
      return '';
    },
    getLiningType(field) {
      if (field) {
        if (field === 'Unlined') return 'No Lining';
        if (field === 'RegLining') return 'Regular';
        if (field === 'SelfLined') return 'Self Lined';
        return field;
      }
      return '';
    },
    getFringesType(field, value) {
      if (value && field === value.replace(/ .*/, '')) {
        return value.replace(/.*? /, '');
      }
      return '';
    },
    getRGQty(field, key, value) {
      if (field === key) {
        return value;
      }
      return '';
    },
    formatStack(field) {
      if (field) {
        switch (field) {
          case '1wayR':
            return 'One Way Right';
          case '1wayL':
            return 'One Way Left';
          default:
            return field;
        }
      }
      return '';
    },
    formatFullness(field) {
      if (field) {
        if (/\.00%$/.test(field)) {
          return field.replace(/\.00%$/, '%');
        }
        return field;
      }
      return '';
    },
    makeColumns(detail, category) {
      switch (category) {
        case 'shades':
          return this.makeShadesColumns(detail);
        case 'fab shades':
          return this.makeFabShadesColumns(detail);
        case 'drapes':
          return this.makeDrapesColumns(detail);
        case 'parts':
          return this.makePartsColumns(detail);
        case 'upholstery':
          return this.makeUpholsteryColumns(detail);
        case 'motorized':
          return this.makeMotorizedColumns(detail);
        default:
          return [];
      }
    },
    makeShadesColumns(d) {
      const cols = [{
        _name: `${d.Zebra ? 'Zebra ' : ''}Roller Shade`,
        _qty: `Qty. ${d.Qty}`,
        _size: `${d.Width} x ${d.Height} ${d.IBOB}`,
        _room: d.Room ? `Room: ${d.Room}` : '',
        _meta_is_narrow: 'is-narrow',
      }];
      let fabricPosition = '';
      if (d['Fabric Position'] === 'Reg') {
        fabricPosition = 'Regular';
      } else if (d['Fabric Position'] === 'Rev') {
        fabricPosition = 'Reverse';
      }
      let col2 = {
        'Fabric Style': d.Style,
        'Fabric Color': d.Color,
        'Fabric Position': fabricPosition,
      };
      if (d.Motorized) {
        col2 = {
          ...col2,
          'Motor Type': d['Motor Type'],
          Control: d.Control,
        };
      } else {
        col2 = {
          ...col2,
          'Chain Type': this.getChainType(d.Chain),
          'Chain Color': this.getChainColor(d.Chain),
          'Chain Control': this.getChainControl(d['Chain Control']),
          'Chain Length': d['Chain Length'],
        };
      }
      cols.push(col2);
      let col3 = {
        Valance: d.Valance,
        'Valance Color': d['Valance Color'],
        'Valance Size': d['Valance Size'],
      };
      if (d.Valance && d.Valance.toLowerCase().includes('fascia')) {
        col3 = {
          ...col3,
          'Fascia End Caps': d['Fascia End Caps'] ? 'Yes' : 'No',
        };
      }
      cols.push(col3);
      cols.push({
        'Hardware Color': d['Hardware Color'],
        'Side Channel': d['Side Channel'],
        'Bottom Channel': d.BottomChannel,
        'Hembar Color': d['Hembar Color'],
        'Hembar Pocket': d['Hembar Pocket'],
      });
      return this.removeEmptyFields(cols);
    },
    makeFabShadesColumns(d) {
      const cols = [
        {
          _roman_type: d['Roman Type'], _qty: `Qty. ${d.Qty}`, _size: `${d.Width} x ${d.Height} ${d.IBOB}`, Room: d.Room, _meta_is_narrow: 'is-narrow',
        },
        { Fabric: d.Color, 'Fabric Color': d.Color, Fringes: d.Fringes },
        { 'Chain Type': this.getChainType(d.Chain), 'Chain Color': this.getChainColor(d.Chain), 'Chain Control': d['Chain Control'] === 'R' ? 'Right' : 'Left' },
      ];
      if (d.Valance) {
        const col = {
          Valance: d.Valance,
          'Valance Color': d['Valance Color'],
          'Side Channel': d['Side Channel'],
          'Bottom Channel': d.BottomChannel,
        };
        if (col.Valance && col.Valance.toLowerCase().startsWith('fascia')) {
          col['Fascia End Caps'] = d['Fascia End Caps'] ? 'Yes' : 'No';
        }
        cols.push(col);
      }
      return this.removeEmptyFields(cols);
    },
    makeDrapesColumns(d) {
      const cols = [
        {
          _drape_type: d['Drape Type'] ? d['Drape Type'].replace(/^\w/, c => c.toUpperCase()) : '',
          _qty: `Qty. ${d.Qty}`,
          _size: `${d.Width} x ${d.Height} ${d.IBOB}`,
          Room: d.Room,
          _meta_is_narrow: 'is-narrow',
        },
        // Width,Chain Weight,returns,Color,R/G Qty,Room,IBOB,Widths,Overlap,Qty,
        // Stack,Rings/Grommets,Drape Type,Fullness,Split,Snaps,Height,Lining,Fringes
        {
          Widths: d.Widths,
          Stack: this.formatStack(d.Stack),
          Fullness: d.Fullness,
          Returns: d.returns,
          Overlap: d.Overlap,
          Snaps: d.Snaps,
        },
        {
          Lining: this.getLiningType(d.Lining),
          Fabric: d.Color,
          Banding: this.getFringesType('Banding', d.Fringes),
          Trimming: this.getFringesType('Trimming', d.Fringes),
        },
        {
          Rings: this.getRGQty('Rings', d['Rings/Grommets'], d['R/G Qty']),
          Grommets: this.getRGQty('Grommets', d['Rings/Grommets'], d['R/G Qty']),
        },
      ];
      return this.removeEmptyFields(cols);
    },
    makePartsColumns(d) {
      const cols = [
        {
          _Item: d.Item, _qty: `Qty. ${d.Qty}`, _meta_is_narrow: 'is-narrow',
        },
        {
          Description: d.Description,
        },
      ];
      return this.removeEmptyFields(cols);
    },
    makeUpholsteryColumns(d) {
      const cols = [
        {
          _qty: `Qty. ${d.Qty}`, _size: `${d.Width} x ${d.Height} ${d.IBOB}`, Room: d.Room, _meta_is_narrow: 'is-narrow',
        },
      ];
      return this.removeEmptyFields(cols);
    },
    makeMotorizedColumns(d) {
      const cols = [
        {
          _qty: `Qty. ${d.Qty}`, _size: `${d.Width} x ${d.Height} ${d.IBOB}`, Room: d.Room, _meta_is_narrow: 'is-narrow',
        },
      ];
      return this.removeEmptyFields(cols);
    },
    async submit() {
      const result = await submitOrder({
        quoteAmount: JSON.parse(window.localStorage.getItem('ed-cart-quote-amount')),
        quoteNumber: JSON.parse(window.localStorage.getItem('ed-cart-quote-number')),
        po: JSON.parse(window.localStorage.getItem('ed-cart-po')),
        notes: JSON.parse(window.localStorage.getItem('ed-cart-notes')),
        details: JSON.parse(window.localStorage.getItem('ed-cart-items')),
        operationSystem: JSON.parse(window.localStorage.getItem('ed-cart-operation-system')),
      });
      console.log(result);

      window.localStorage.removeItem('ed-cart-po');
      window.localStorage.removeItem('ed-cart-quote-number');
      window.localStorage.removeItem('ed-cart-operation-system');
      window.localStorage.removeItem('ed-cart-items');
      window.localStorage.removeItem('ed-cart-category');
      window.localStorage.removeItem('ed-cart-quote-amount');
      window.localStorage.removeItem('ed-cart-po');
      window.localStorage.removeItem('ed-cart-last-id');
      window.localStorage.removeItem('ed-cart-notes');
      this.$router.push({ path: '/' });
    },
  },
};
</script>

<style scoped>
.is-center {
  justify-content: center;
}
.ed-si-box {
  margin: 2em 12em 2em 12em;
}

.ed-ai-box {
  margin: 1em 12em 1em 12em;
  padding: 0;
}
.ed-ai-header {
  margin: 2em 0em 0em 0em;
  padding: 0;
}

.order-header {
  margin-bottom: 1em;
}
</style>
